import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";

const CARDS = [
  {
    title: "Mainland",
    copy: "Set up a mainland company in the UAE through the Department of Economic Development (DED) and gain unrestricted access to the local UAE market, prime business locations and government contracts. Mainland companies allow 100% foreign ownership in most sectors.",
    image:
      "https://images.unsplash.com/photo-1542314831-068cd1dbfeeb?auto=format&fit=crop&w=1200&q=80",
    to: "/company-formation/mainland",
    tag: "01 / Mainland",
  },
  {
    title: "Freezone",
    copy: "UAE free zones offer 100% foreign ownership, 0% corporate tax on qualifying income and the freedom to operate internationally. With 45+ freezones available — DMCC, IFZA, RAKEZ and more — foreign investors can find a jurisdiction that matches their industry.",
    image:
      "https://images.unsplash.com/photo-1582407947304-fd86f028f716?auto=format&fit=crop&w=1200&q=80",
    to: "/company-formation/freezone",
    tag: "02 / Freezone",
  },
  {
    title: "Offshore",
    copy: "Register an offshore company in the UAE through designated jurisdictions such as RAK ICC or JAFZA to benefit from 0% tax, full confidentiality and no requirement for a physical office. Ideal for international trading, asset protection and holding companies.",
    image:
      "https://images.unsplash.com/photo-1499678329028-101435549a4e?auto=format&fit=crop&w=1200&q=80",
    to: "/company-formation/freezone",
    tag: "03 / Offshore",
  },
];

export default function JurisdictionsV2() {
  return (
    <section
      id="jurisdictions"
      data-testid="v2-jurisdictions"
      className="relative bg-[#F8FAFC] py-10 md:py-14"
    >
      <div className="container-x">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-7">
          <div className="max-w-2xl">
            <p className="overline text-[#3366FF]">Select your jurisdiction</p>
            <div className="gold-rule mt-5 mb-6" />
            <h2 className="font-heading font-light tracking-tight text-[#0F172A] text-2xl sm:text-3xl lg:text-[2.5rem] leading-tight">
              Three routes into the UAE —{" "}
              <span className="font-black text-[#002366]">one of them is yours</span>.
            </h2>
          </div>
          <p className="md:max-w-md text-slate-600 leading-relaxed">
            Each jurisdiction unlocks a different mix of ownership, tax posture,
            market access and office requirements. We match yours in the first
            20-minute discovery call.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {CARDS.map((c, i) => (
            <motion.article
              key={c.title}
              data-testid={`v2-jurisdiction-${i}`}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.7, delay: i * 0.12, ease: [0.16, 1, 0.3, 1] }}
              className="group relative overflow-hidden rounded-sm bg-white border border-slate-200 hover:shadow-2xl hover:border-[#3366FF]/40 transition-all duration-500"
            >
              <div className="relative aspect-[5/3] overflow-hidden">
                <img
                  src={c.image}
                  alt={c.title}
                  className="w-full h-full object-cover transition-transform duration-[1400ms] ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-110"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#002366]/75 via-[#002366]/10 to-transparent" />
                <span className="absolute top-5 left-5 chip-dark !text-white !bg-white/10 !border-white/20">
                  {c.tag}
                </span>
                <h3 className="absolute left-6 bottom-6 right-6 font-heading font-black text-white text-3xl md:text-4xl tracking-tight">
                  {c.title}
                </h3>
              </div>
              <div className="p-7">
                <p className="text-slate-600 leading-relaxed text-sm md:text-base min-h-[140px]">
                  {c.copy}
                </p>
                <Link
                  to={c.to}
                  className="mt-6 inline-flex items-center gap-2 font-heading font-semibold text-[#002366] group-hover:text-[#3366FF] transition-colors"
                >
                  Learn more
                  <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </Link>
              </div>
              <div className="h-1 w-full bg-[#3366FF] scale-x-0 origin-left group-hover:scale-x-100 transition-transform duration-700" />
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}
